/**
 * RegionBrushManager
 *
 * Manages the brush cursor and region selection state for depth segmentation.
 * Handles brush cursor rendering, region ID tracking, and brush size adjustment.
 */

import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { Viewer } from '../../../../viewer/viewer';
import { CoordinateTransformer } from '../utils/CoordinateTransformer';

export class RegionBrushManager {
  /** Set of confirmed region IDs */
  readonly selectedRegionIds: Set<number> = new Set();

  /** Whether a drag-paint operation is in progress */
  regionDragging = false;

  /** When true, drainPolygonQueue auto-confirms on completion */
  autoConfirmPending = false;

  /** Brush rectangle half-size in screen pixels */
  brushHalfSize = 15;

  /** Brush cursor mesh (screen-aligned rectangle) */
  private brushCursorMesh: Line2 | null = null;

  readonly BRUSH_SIZE_MIN = 10;
  readonly BRUSH_SIZE_MAX = 200;

  constructor(
    private viewer: Viewer,
    private coordinateTransformer: CoordinateTransformer,
  ) {}

  /**
   * Add region IDs to the confirmed selection (local tracking only).
   * Returns true if any new IDs were added.
   */
  addRegions(regionIds: number[]): boolean {
    let hasNew = false;
    for (const id of regionIds) {
      if (!this.selectedRegionIds.has(id)) {
        this.selectedRegionIds.add(id);
        hasNew = true;
      }
    }
    return hasNew;
  }

  /**
   * Replace the entire selection with the given set (worker-authoritative).
   */
  setRegionIds(ids: Set<number>): void {
    this.selectedRegionIds.clear();
    for (const id of ids) {
      this.selectedRegionIds.add(id);
    }
  }

  /**
   * Clear the region selection.
   */
  clearSelection(): void {
    this.selectedRegionIds.clear();
    this.autoConfirmPending = false;
  }

  /**
   * Render or update the brush cursor rectangle at the given screen position.
   * Converts screen-space radius to world-space using the orthographic camera.
   */
  renderBrushCursor(screenPos: THREE.Vector2): void {
    const camera = this.viewer.scene.getActiveCamera();
    if (camera.type !== 'OrthographicCamera') return;

    const orthoCamera = camera as THREE.OrthographicCamera;
    const worldPos = this.coordinateTransformer.screenToWorld(screenPos);
    if (!worldPos) return;

    // Convert screen-pixel half-size to world-space half-size
    const viewportWidth = this.viewer.renderArea.clientWidth;
    const orthoWidth = orthoCamera.right - orthoCamera.left;
    const worldHalfSize = (this.brushHalfSize / viewportWidth) * orthoWidth;

    if (!this.brushCursorMesh) {
      // Create closed rectangle using Line2 for thick line support
      // 5 points: 4 corners + close back to first
      const geometry = new LineGeometry();
      geometry.setPositions([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0, -1, -1, 0]);
      const material = new LineMaterial({
        color: 0x702ecf,
        linewidth: 4,
        depthTest: false,
        transparent: true,
        opacity: 1.0,
        resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
      });
      this.brushCursorMesh = new Line2(geometry, material);
      this.brushCursorMesh.renderOrder = 1000;
      this.viewer.scene.scene.add(this.brushCursorMesh);
    }

    // Update resolution in case viewport was resized
    (this.brushCursorMesh.material as LineMaterial).resolution.set(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight);

    // Update position, scale, and match camera orientation so the rect
    // stays screen-aligned. Offset slightly toward the camera (along
    // -view-direction) so the rect renders on top of the cloud instead
    // of into it. Pre-fix this added +0.1 to world Z, which lifted the
    // rect upward on a top-down camera (matched intent) but offset it
    // VERTICALLY off the wall plane on a wall-perpendicular camera
    // (broke alignment with the cursor / brush mask).
    const towardCamera = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion).negate();
    const offset = towardCamera.multiplyScalar(0.1);
    this.brushCursorMesh.position.set(worldPos.x + offset.x, worldPos.y + offset.y, worldPos.z + offset.z);
    this.brushCursorMesh.scale.set(worldHalfSize, worldHalfSize, 1);
    this.brushCursorMesh.quaternion.copy(camera.quaternion);
    this.brushCursorMesh.visible = true;
  }

  /**
   * Remove and dispose the brush cursor mesh.
   */
  clearBrushCursor(): void {
    if (this.brushCursorMesh) {
      this.viewer.scene.scene.remove(this.brushCursorMesh);
      this.brushCursorMesh.geometry.dispose();
      (this.brushCursorMesh.material as THREE.Material).dispose();
      this.brushCursorMesh = null;
    }
  }

  /**
   * Compute brush half-size in mask pixels from screen pixels.
   */
  getBrushHalfSizeInMaskPixels(maskWidth?: number, viewportWidth?: number): number {
    const mw = maskWidth ?? this.viewer.renderArea.clientWidth;
    const vw = viewportWidth ?? this.viewer.renderArea.clientWidth;
    return Math.round(this.brushHalfSize * (mw / vw));
  }

  /**
   * Adjust brush size by a delta, clamped to min/max bounds.
   */
  adjustBrushSize(delta: number): void {
    this.brushHalfSize = Math.max(this.BRUSH_SIZE_MIN, Math.min(this.BRUSH_SIZE_MAX, this.brushHalfSize + delta));
  }

  /**
   * Dispose all resources.
   */
  dispose(): void {
    this.clearBrushCursor();
    this.selectedRegionIds.clear();
  }
}
